import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'relements/build/esm/overlays/modal';
import Dropdown from 'relements/build/esm/input/dropdown';

import { translateBot } from '@src/api/DomainAPI';
import { setURLMetadata } from '@src/actions/SubstoryActions';

import { getLanguageOptions } from './utils';
import ContentRow from './ContentRow';
import styles from './TranslationSettings.scss';

const TranslationSettings = ({
  allLanguages = [],
  supportedLanguages = [],
  counts = {},
  status = {},
  active = false,
  onClose = () => {},
  onChange = () => {},
  domain = {},
  to: defaultTo,
}) => {
  const [sourceLanguages, destinationLanguages] = getLanguageOptions(
    supportedLanguages,
    allLanguages
  );

  const {
    from_language_code: fromLanguageCode = 'en',
    to_language_code: toLanguageCode = undefined,
  } = status;

  let {
    translating_bot_says: translatingBotSays = 0,
    translating_user_says: translatingUserSays = 0,
    translating_started: translatingStarted = false,
  } = status;

  const [from, setFrom] = React.useState(
    sourceLanguages.filter(({ code }) => code === fromLanguageCode)[0]
  );

  const [to, setTo] = React.useState(
    destinationLanguages.filter(({ code }) => code === toLanguageCode)[0]
  );

  const handleSave = React.useCallback(async e => {
    onChange({
      from_language_code: from.code,
      to_language_code: to.code,
      translating_bot_says: 0,
      translating_user_says: 0,
      translating_started: true,
    });

    try {
      await translateBot(domain.title, from, to);
      setURLMetadata(undefined, undefined, true);
      onClose(e);
    } catch (err) {
      onChange({
        from_language_code: from.code,
        to_language_code: to.code,
        translating_bot_says: 2,
        translating_user_says: 2,
        translating_started: false,
      });
    }
  });

  React.useEffect(() => {
    setTo(
      destinationLanguages.filter(
        ({ code }) => code === (defaultTo || toLanguageCode)
      )[0]
    );
  }, [destinationLanguages.length, defaultTo]);

  if (to && toLanguageCode !== to.code) {
    translatingBotSays = 0;
    translatingUserSays = 0;
    translatingStarted = false;
  }

  return (
    <Modal className={styles.translationModal} active={active} onOverlayClick={onClose}>
      <Modal.Header
        onClose={onClose}
        onSave={handleSave}
        title="Translate Bot"
        withSave={!translatingStarted}
        saveTitle="Translate"
      >
        <div style={{ display: 'grid' }}>
          <span>Translate Bot</span>
          <span style={{ fontSize: '13px' }}>
            To initiate, select the language you want to translate from
          </span>
        </div>
      </Modal.Header>

      <div className={styles.translationSettings}>
        <div className={styles.translationSettingsRow}>
          <Dropdown
            withSearch
            value={from || {}}
            onChange={setFrom}
            className={styles.dropdown}
            label="Original Content"
            placeholder="Select Language..."
            options={sourceLanguages}
            optionKey="name"
            disabled={translatingStarted}
          />
          <div className={styles.arrowRight}>→</div>
          <Dropdown
            withSearch
            value={to || {}}
            onChange={setTo}
            className={styles.dropdown}
            label="New Language"
            placeholder="Select Language..."
            options={destinationLanguages}
            optionKey="name"
            disabled={translatingStarted}
          />
          <div className={styles.estimatedTime}>
            <div className={styles.estimatedTimeLabel}>
              Estimated Time Required
            </div>
            <div className={styles.estimatedTimeValue}>50 seconds</div>
          </div>
        </div>

        <div className={styles.translationSettingsProgress}>
          <div className={styles.title}>Content Being Translated</div>
          <div className={styles.subtitle}>
            {counts.nodes}
            {' '}
            steps containing:
          </div>
          <ContentRow started={translatingStarted} status={translatingUserSays}>
            {counts.userSays}
            {' '}
            User Messages
          </ContentRow>
          <ContentRow started={translatingStarted} status={translatingBotSays}>
            {counts.botSays}
            {' '}
            Bot Responses
          </ContentRow>
          {/* <ContentRow>{counts.entities} Entities</ContentRow> */}
        </div>
      </div>
    </Modal>
  );
};

TranslationSettings.propTypes = {
  allLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      languageCode: PropTypes.string,
      languageName: PropTypes.string,
      languageNativeName: PropTypes.string,
    })
  ),
  supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  counts: PropTypes.shape({
    nodes: PropTypes.number,
    userSays: PropTypes.number,
    botSays: PropTypes.number,
    entitites: PropTypes.number,
  }),
  active: PropTypes.bool,
  onClose: PropTypes.func,
  status: PropTypes.shape({
    from_language_code: PropTypes.string,
    to_language_code: PropTypes.string,
    translating_bot_says: PropTypes.oneOf([0, 1, 2]),
    translating_user_says: PropTypes.oneOf([0, 1, 2]),
    translating_started: PropTypes.bool,
  }),
};

TranslationSettings.defaultProps = {
  allLanguages: [],
  supportedLanguages: [],
  counts: {},
  active: false,
  onClose: () => {},
  status: {},
};

export default TranslationSettings;
